/**
 * @file CompanyDeletionSections.tsx
 * @brief Profile sections for company accounts (Apple 5.1.1(v), BACKEND B7.2):
 *        "Request account deletion" (members and admins) and "Delete
 *        company" (admins, web + Mac only).
 *
 * @details
 * Members can't delete anything themselves: they ask, and the admin and
 * VERBLIZR support are emailed. An open request shows "Requested on …, done
 * by …" instead of the button (asking again returns the same request).
 *
 * Delete company is the strongest confirm in the app: the server's warning
 * as is (every member, data, plan cancelled, 30-day erasure), the member
 * count, the company name typed, and the password (empty for Google / Apple
 * / Microsoft accounts; REAUTH_REQUIRED → sign in again here, retry once).
 * B33 (26-10-2026): with 2FA on, REAUTH_REQUIRED asks for a code instead
 * (`DeleteReauth.tsx`); a sign-in again keeps this section open through the
 * code step.
 * Success revokes every token: show the message, then sign out locally.
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { useState } from "react";
import { Building2, LoaderCircle, Lock, Send, Trash2, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { GridBand, GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { BareField } from "@/features/profile/mobileForm";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import type { AuthUser } from "@/shared/types/api";
import { formatDate } from "@/shared/utils/formatters";
import { companyNameMatches, requestStatusLine } from "./accountDeletion";
import { useCompanyDeletion, useDeleteCompany, useMyDeletionRequest, useRequestDeletion } from "./api";
import { ReauthCodeField, ReauthSignIn } from "./DeleteReauth";
import { useDeleteReauth } from "./useDeleteReauth";

const errText = (err: unknown) => (err instanceof ApiClientError ? err.message : "Please try again.");

/** Company member (or admin, for their own account): ask for deletion. */
export function RequestDeletionSection({ isAdmin, onClose }: { isAdmin: boolean; onClose: () => void }) {
  const mine = useMyDeletionRequest(true);
  const ask = useRequestDeletion();
  const [reason, setReason] = useState("");
  const open = mine.data;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const done = await ask.mutateAsync({ reason });
      toast({ variant: "success", title: "Request sent", description: done.message });
      setReason("");
    } catch (err) {
      toast({ variant: "destructive", title: "Couldn't send your request", description: errText(err) });
    }
  };

  return (
    <div className="border-t border-border/60">
      <GridBand className="text-sm font-medium">
        {isAdmin ? "Request deletion of my account" : "Request account deletion"}
        <span className="ml-1 font-normal text-muted-foreground">
          · Your company manages this account, so you can&rsquo;t delete it yourself. We&rsquo;ll send your
          request to your company administrator and VERBLIZR support, and your account will be deleted
          within 30 days. Meetings that belong to your company stay with the company.
        </span>
      </GridBand>
      {mine.isLoading ? (
        <GridCell dense className="border-0 text-sm text-muted-foreground">Loading…</GridCell>
      ) : open ? (
        <GridCell padded={false} className="border-b-0">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 md:px-5">
            <p className="text-sm">
              <span className="font-medium">Deletion requested.</span>{" "}
              <span className="text-muted-foreground">{requestStatusLine(open, (iso) => formatDate(iso))}</span>
            </p>
            <Button type="button" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </GridCell>
      ) : (
        <form onSubmit={submit}>
          <GridCell dense className="border-0">
            <BareField
              id="deletionReason"
              label="Reason (optional)"
              icon={<TriangleAlert className="h-5 w-5" strokeWidth={1.75} />}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              autoComplete="off"
              last
            />
          </GridCell>
          <GridCell padded={false} className="border-b-0">
            <div className="flex justify-end gap-2 px-4 py-3 md:px-5">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={ask.isPending} className="gap-2">
                {ask.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send request
              </Button>
            </div>
          </GridCell>
        </form>
      )}
    </div>
  );
}

/** Company admin: delete the whole company. */
export function DeleteCompanySection({ onClose }: { onClose: () => void }) {
  const { user, logout } = useAuth();
  const info = useCompanyDeletion(true);
  const del = useDeleteCompany();
  const [typed, setTyped] = useState("");
  const [password, setPassword] = useState("");
  const reauth = useDeleteReauth();
  const data = info.data;
  const matches = companyNameMatches(typed, data?.companyName);

  const run = async (afterReauth: boolean) => {
    const proof = afterReauth ? {} : reauth.proof();
    if (!proof) return;
    try {
      const done = await del.mutateAsync({ confirm: typed.trim(), currentPassword: password, ...proof });
      reauth.reset();
      toast({ variant: "success", title: "Company deletion started", description: done.message });
      await logout();
    } catch (err) {
      if (reauth.handleError(err, afterReauth)) return;
      toast({ variant: "destructive", title: "Couldn't delete the company", description: errText(err) });
    }
  };

  // The fresh sign-in must be the same admin, or the retry would act for whoever signed in.
  const onReauth = (signedIn: AuthUser) => {
    if (signedIn.id !== user?.id) {
      reauth.reset();
      toast({
        variant: "destructive",
        title: "That was a different account",
        description: `You are now signed in as ${signedIn.email}. Nothing was deleted.`,
      });
      return;
    }
    void run(true);
  };

  return (
    <div className="border-t border-destructive/40">
      <GridBand className="text-sm font-medium text-destructive">Delete company</GridBand>
      {!data ? (
        <GridCell dense className="border-0 text-sm text-muted-foreground">
          {info.isError ? `Couldn't load this. ${errText(info.error)}` : "Loading…"}
        </GridCell>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (matches) void run(false);
          }}
        >
          <GridCell dense className="border-0">
            <div className="flex gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div className="space-y-1.5">
                <p className="font-medium">
                  {data.companyName} · {data.memberCount} {data.memberCount === 1 ? "member" : "members"}
                </p>
                <p className="text-muted-foreground">{data.warning}</p>
              </div>
            </div>
          </GridCell>
          <GridCells className="grid-cols-1 sm:grid-cols-2">
            <GridCell dense className="border-0">
              <BareField
                id="deleteCompanyName"
                label={`${data.confirmHint} (${data.companyName})`}
                icon={<TriangleAlert className="h-5 w-5" strokeWidth={1.75} />}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
              />
            </GridCell>
            <GridCell dense className="border-0">
              <BareField
                id="deleteCompanyPassword"
                label="Password (leave empty if you use Google, Apple or Microsoft)"
                icon={<Lock className="h-5 w-5" strokeWidth={1.75} />}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </GridCell>
          </GridCells>
          {reauth.mode === "code" ? (
            <GridCell dense className="border-0">
              <ReauthCodeField reauth={reauth} busy={del.isPending} />
            </GridCell>
          ) : null}
          <GridCell padded={false} className="border-b-0">
            <div className="flex justify-end gap-2 px-4 py-3 md:px-5">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  reauth.reset();
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={del.isPending || !matches} className="gap-2">
                {del.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete company and all accounts
              </Button>
            </div>
          </GridCell>
        </form>
      )}
      {/* Outside the form: the 2FA step that may follow is a form of its own. */}
      {data && reauth.mode === "signIn" ? (
        <GridCell dense className="border-0">
          <ReauthSignIn reauth={reauth} busy={del.isPending} then="the company is deleted" onSignedIn={onReauth} />
        </GridCell>
      ) : null}
    </div>
  );
}
